import { useEffect, useRef, useState } from "react";

/**
 * Progression simulée façon ElevenLabs.
 *
 * Quand une action de durée inconnue démarre (`active` = true), la barre file
 * vite vers ~90 %, puis ralentit de façon asymptotique (jamais bloquée à 100 %).
 * Dès que l'action se termine (`active` repasse à false), elle complète d'un
 * coup jusqu'à 100 %, marque une courte pause, puis disparaît en fondu.
 *
 * Respecte `prefers-reduced-motion` : dans ce cas la barre n'apparaît jamais
 * (`enabled` = false) et l'appelant fournit un repli sans animation. La
 * préférence est stockée en state pour forcer un re-render à sa détection et à
 * chacun de ses changements (et pour pouvoir annuler une animation en cours).
 *
 * @param {boolean} active - true tant que l'action est en cours (ex. isLoading)
 * @returns {{ mounted: boolean, pct: number, visible: boolean, enabled: boolean }}
 *   mounted  : faut-il rendre la barre dans le DOM
 *   pct      : avancement 0..100 (largeur révélée)
 *   visible  : opacité 1 (true) ou 0 (false, fondu de sortie)
 *   enabled  : l'animation est-elle autorisée (false si reduced-motion)
 */
export default function useSimulatedProgress(active) {
    const [state, setState] = useState({
        mounted: false,
        pct: 0,
        visible: false,
    });
    const [reduce, setReduce] = useState(false);

    const rafRef = useRef(null);
    const timerRef = useRef(null);
    const pctRef = useRef(0);
    const phaseRef = useRef("idle"); // idle | ramp | complete
    const wasActiveRef = useRef(false);

    // Détection de prefers-reduced-motion (client uniquement). En state pour
    // déclencher un re-render à la détection et à chaque changement.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduce(mq.matches);
        const onChange = (e) => setReduce(e.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);

    useEffect(() => {
        const clearRaf = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
        const clearTimer = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
        };

        // Reduced motion : on annule toute animation en cours et on reste au
        // repos (l'appelant affiche son repli statique).
        if (reduce) {
            clearRaf();
            clearTimer();
            phaseRef.current = "idle";
            pctRef.current = 0;
            wasActiveRef.current = false;
            setState((s) =>
                s.mounted ? { mounted: false, pct: 0, visible: false } : s,
            );
            return undefined;
        }

        if (active) {
            // Démarrage (ou redémarrage) de la rampe vers ~90 %
            clearTimer();
            clearRaf();
            phaseRef.current = "ramp";
            wasActiveRef.current = true;
            pctRef.current = 0;
            setState({ mounted: true, pct: 0, visible: true });

            const ramp = () => {
                pctRef.current += (90 - pctRef.current) * 0.045;
                setState({ mounted: true, pct: pctRef.current, visible: true });
                rafRef.current = requestAnimationFrame(ramp);
            };
            rafRef.current = requestAnimationFrame(ramp);

            return clearRaf;
        }

        // active === false : on complète seulement si on sortait d'une rampe
        if (wasActiveRef.current && phaseRef.current === "ramp") {
            wasActiveRef.current = false;
            phaseRef.current = "complete";
            clearRaf();

            const complete = () => {
                pctRef.current += (100 - pctRef.current) * 0.3;
                if (pctRef.current > 99.5) {
                    pctRef.current = 100;
                    clearRaf();
                    setState({ mounted: true, pct: 100, visible: true });
                    // Maintien plein, puis fondu, puis démontage
                    timerRef.current = setTimeout(() => {
                        setState({ mounted: true, pct: 100, visible: false });
                        timerRef.current = setTimeout(() => {
                            phaseRef.current = "idle";
                            pctRef.current = 0;
                            setState({ mounted: false, pct: 0, visible: false });
                        }, 380);
                    }, 320);
                } else {
                    setState({
                        mounted: true,
                        pct: pctRef.current,
                        visible: true,
                    });
                    rafRef.current = requestAnimationFrame(complete);
                }
            };
            rafRef.current = requestAnimationFrame(complete);

            return clearRaf;
        }

        return undefined;
    }, [active, reduce]);

    // Nettoyage au démontage
    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    if (reduce) {
        return { mounted: false, pct: 0, visible: false, enabled: false };
    }
    return { ...state, enabled: true };
}
